import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';

import './styles/search-box.css';

// The panel's search box, shared by every list whose endpoint takes `?q=`.
//
// It writes to the URL and reads from it, so the query is part of the address:
// a link to "agro" on page 3 survives a reload and can be sent to somebody.
//
// TYPING IS DEBOUNCED, the URL is not written on every keystroke. Without it
// each letter is a request and a history entry, and the back button turns into
// an undo for typing.
//
// Changing the search RESETS the page. Staying on page 3 of a narrower result
// lands on an empty screen that reads as "there is nothing" when there is.
//
// Under two characters the backend ignores `q` and answers the plain list, so
// there is no "keep typing" state to invent here — the list simply comes back.

export default function SearchBox({ placeholder = 'Buscar...' }) {
    const [params, setParams] = useSearchParams();
    const urlQuery = params.get('q') || '';

    // The input is its own state so typing is never held back by the round
    // trip; the URL catches up when the person stops.
    const [text, setText] = useState(urlQuery);
    const [lastUrlQuery, setLastUrlQuery] = useState(urlQuery);

    // Someone else changed the URL — the back button, a link, the clear button.
    // Adjusted DURING RENDER and not in an effect: React re-runs this component
    // before painting, so nothing flashes, and a setState inside an effect
    // would be the cascading render the template's lint rejects.
    if (urlQuery !== lastUrlQuery) {
        setLastUrlQuery(urlQuery);
        setText(urlQuery);
    }

    useEffect(() => {
        if (text === urlQuery) return undefined;

        const timer = setTimeout(() => {
            setParams((prev) => {
                const next = new URLSearchParams(prev);
                if (text) next.set('q', text);
                else next.delete('q');
                next.delete('page');
                return next;
            }, { replace: true });
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [text, urlQuery, setParams]);

    return (
        <div className="search-box">
            <input
                className="search-box__input"
                type="search"
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder={placeholder}
                aria-label={placeholder}
            />

            {urlQuery && (
                <button
                    type="button"
                    className="search-box__clear"
                    onClick={() => setText('')}
                >
                    Limpiar
                </button>
            )}
        </div>
    );
}
